import * as Yup from 'yup';
import { z } from 'zod';
import { type ReceivingSession, ReceivingSessionSchema } from './receiving';

// Zod schemas for the payload (NOT the wire message) of each inbound
// receiving-related WS action. The WS layer owns the `action` field — these
// schemas intentionally contain only domain fields.

// TEMPORARY, and the reason this file could move without dragging
// data/receiving.ts along: the transport is typed on Zod (ORD-441) while
// ReceivingSessionSchema is still Yup, and Zod cannot embed a Yup schema.
//
// This validates the nested session the way the provider used to validate the
// whole frame — synchronously and strict, so nothing is coerced — and hands the
// value through untouched rather than returning Yup's cast output, which keeps
// today's behaviour exactly: no defaults filled in, no unknown keys stripped.
// Yup's own message is forwarded, because a rejected frame stalls a receiving
// session silently and the RUM error is the only thing that explains why.
//
// ORD-425 migrates data/receiving.ts; when it lands, delete this and write
// `session: ReceivingSessionSchema`.
const zReceivingSession = z.unknown().transform((value, ctx): ReceivingSession => {
  if (value == null) {
    ctx.addIssue({ code: 'custom', message: 'session is a required field' });

    return z.NEVER;
  }

  try {
    ReceivingSessionSchema.validateSync(value, { strict: true });
  } catch (error) {
    ctx.addIssue({
      code: 'custom',
      message: error instanceof Yup.ValidationError ? error.errors.join('; ') : String(error),
    });

    return z.NEVER;
  }

  return value as ReceivingSession;
});

export const receivingSessionUpdatedPayloadSchema = z.object({
  session: zReceivingSession,
  // Transient one-shot hint, set by the backend only when a Resume probe was
  // refused (companion replied rcv_resume_result{ready:false}). Never persisted
  // on the session; absent on every routine broadcast.
  probe_refused: z.boolean().default(false),
  // Abandon-side analogue of probe_refused: set only when the companion refused
  // the Abandon clear with rcv_clear_result{busy:true} (LPOS holds the file —
  // session kept alive unchanged). Never persisted; absent on routine broadcasts.
  abandon_busy: z.boolean().default(false),
});

export type ReceivingSessionUpdatedPayload = z.infer<typeof receivingSessionUpdatedPayloadSchema>;

export const companionStatusChangedPayloadSchema = z.object({
  machine_id: z.string(),
  connected: z.boolean(),
});

export type CompanionStatusChangedPayload = z.infer<typeof companionStatusChangedPayloadSchema>;

export const sessionLeaseReleasedPayloadSchema = z.object({
  session_id: z.string(),
});

export type SessionLeaseReleasedPayload = z.infer<typeof sessionLeaseReleasedPayloadSchema>;

export const storeUpdatedPayloadSchema = z.object({
  store_id: z.string(),
  // RFC3339 string (NOT a date schema): the WS provider parses without
  // coercion, under which a date schema would reject the string and skip the
  // handler. Optional/nullable so an older backend that omits it still
  // validates.
  last_uploaded_at: z.string().nullable().optional(),
});

export type StoreUpdatedPayload = z.infer<typeof storeUpdatedPayloadSchema>;
